import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { GuildCard } from '../components/GuildCard.js';
import { LanguageSwitcher } from '../components/LanguageSwitcher.js';
import { UserMenu } from '../components/UserMenu.js';
import { ApiError, api } from '../lib/api.js';
import { useBotAdd, useGuilds, useMe, type MeResponse } from '../lib/queries.js';

export const Route = createFileRoute('/')({
  beforeLoad: async () => {
    try {
      await api<MeResponse>('/api/auth/me');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        throw redirect({ to: '/login' });
      }
      throw err;
    }
  },
  component: HomePage,
});

function HomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const me = useMe();
  const guildsQuery = useGuilds();
  const botAdd = useBotAdd();

  const managed = guildsQuery.data?.filter((g) => g.botInGuild) ?? [];
  const addable = guildsQuery.data?.filter((g) => !g.botInGuild) ?? [];

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <header className="border-b border-zinc-800 bg-zinc-950/95 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2 text-lg font-semibold">
            <img src="/icon.png" alt="" className="h-7 w-7" />
            {t('app.name')}
          </Link>
          <div className="flex items-center gap-3">
            {me.data?.isSuperAdmin ? (
              <Link
                to="/admin"
                className="rounded-md border border-rose-700/60 bg-rose-950/40 px-2.5 py-1 text-xs font-semibold uppercase tracking-wider text-rose-300 hover:bg-rose-900/40"
              >
                {t('admin.navLink')}
              </Link>
            ) : null}
            <LanguageSwitcher />
            {me.data ? <UserMenu user={me.data} /> : null}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-8">
        {botAdd.recentlyAddedGuildId ? (
          <div className="mb-6 rounded-md border border-emerald-700 bg-emerald-900/20 px-4 py-3 text-sm text-emerald-200">
            {t('home.added', {
              name:
                guildsQuery.data?.find((g) => g.id === botAdd.recentlyAddedGuildId)?.name ??
                botAdd.recentlyAddedGuildId,
            })}
            <button
              type="button"
              onClick={botAdd.clearAdded}
              className="ml-3 underline opacity-80 hover:opacity-100"
            >
              {t('common.dismiss')}
            </button>
          </div>
        ) : null}

        <Section
          title={t('home.managed.title')}
          empty={
            guildsQuery.isLoading
              ? t('common.loading')
              : managed.length === 0
                ? t('home.managed.empty')
                : null
          }
        >
          {managed.map((g) => (
            <GuildCard
              key={g.id}
              guild={g}
              highlighted={g.id === botAdd.recentlyAddedGuildId}
              onClick={() => {
                void navigate({ to: '/guilds/$guildId', params: { guildId: g.id } });
              }}
            />
          ))}
        </Section>

        <Section
          title={t('home.add.title')}
          empty={guildsQuery.isLoading ? null : addable.length === 0 ? t('home.add.empty') : null}
        >
          {addable.map((g) => {
            const pending = botAdd.pendingGuildId === g.id;
            return (
              <div
                key={g.id}
                className="flex items-center gap-4 rounded-xl border border-zinc-800 bg-zinc-900/40 p-4"
              >
                <div className="flex-1 truncate text-sm">{g.name}</div>
                <button
                  type="button"
                  disabled={pending}
                  onClick={() => {
                    void botAdd.startBotAdd(g.id);
                  }}
                  className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
                >
                  {pending ? t('home.add.pending') : t('home.add.button')}
                </button>
              </div>
            );
          })}
        </Section>
      </main>
    </div>
  );
}

function Section(props: { title: string; empty: string | null; children: React.ReactNode }) {
  return (
    <section className="mb-10">
      <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-zinc-400">
        {props.title}
      </h2>
      {props.empty ? (
        <p className="rounded-md border border-zinc-800 bg-zinc-900/40 p-6 text-sm text-zinc-400">
          {props.empty}
        </p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">{props.children}</div>
      )}
    </section>
  );
}
